'use client';

import { createPortal } from 'react-dom';
import CreativeEditorSDK from '@cesdk/cesdk-js';
import { AssetDefinition, Source } from '@cesdk/engine';
import { useToastNotificationContext } from '@sendible/design-system/src';
import { NotificationTypeEnum } from '@sendible/design-system/src/components/molecules/Toast';
import { fetchWithHeaders, reportErrorToFaro } from '@sendible/common';
import { useTranslation } from 'react-i18next';
import { EVENTS, subscribe } from '@sendible/event-manager';
import CreativeEditor from '@cesdk/cesdk-js/react';
import {
  BlurAssetSource,
  ImageColorsAssetSource,
  ColorPaletteAssetSource,
  FiltersAssetSource,
  PagePresetsAssetSource,
  StickerAssetSource,
  TextAssetSource,
  TextComponentAssetSource,
  TypefaceAssetSource,
  VectorShapeAssetSource,
  UploadAssetSources,
} from '@cesdk/cesdk-js/plugins';
import React, { useCallback, useRef } from 'react';
import { CeSDKWrapper } from './index.styles';
import { PhotoEditorConfig } from './PhotoEditor/plugin';
import customCropPresets from './PhotoEditor/CustomFormats.json';
import { getUrlWithCredentialsFromUser } from '../../utils';
import { useQueryMediaEndpontForSourceSets } from '../../models/medias';
import { useUploadsContext } from '../../models/uploads/uploadsContext';
import { EDITOR_EXPORT, EDITOR_CLOSE } from '.';

const MemoCreativeEditor = React.memo(CreativeEditor);

type EditorProps = {
  data: OpenEditorDataType;
  resetState: () => void;
};

type SourceSetsType = { url: string; height: string; width: string }[];

const Editor = ({ data, resetState }: EditorProps) => {
  const {
    domainUrl,
    user,
    media: originalMediaObject,
    originalMediaId,
    serviceSet,
    restrictedServices,
    fileName,
    isNewMediaSystem,
    originalImage,
    halfSizeImage,
    quarterSizeImage,
  } = data;

  const { data: media, isSuccess } = useQueryMediaEndpontForSourceSets(originalMediaId, user, isNewMediaSystem);
  const { showToastNotification } = useToastNotificationContext();
  const { t } = useTranslation('media_library');
  const { uploadFiles } = useUploadsContext();

  const mediaRef = useRef({ media, isSuccess });

  React.useEffect(() => {
    mediaRef.current = { media, isSuccess };
  }, [media, isSuccess]);

  const restrictedGroups = new Set(
    Object.entries(restrictedServices ?? {})
      .filter(([, isRestricted]) => isRestricted)
      .map(([service]) => service)
  );

  const oldMediaSystemExportSourceSet = [halfSizeImage, quarterSizeImage, originalImage];
  const oldMediaSystemEditingSourceSet =
    originalImage && halfSizeImage
      ? [
          halfSizeImage,
          quarterSizeImage,
          {
            height: originalImage.height,
            width: originalImage.width,
            // We want to use a smaller resolution image instead of the full resolution during editing.
            // By passing the original height/width, we ensure that we still got the proper dimensions if crop presets are used.
            uri: halfSizeImage.uri,
          },
        ]
      : [];

  const originalImageSource = isNewMediaSystem
    ? {
        uri: originalMediaObject?.originalUrl as string,
        width: originalMediaObject?.metadata.width as number,
        height: originalMediaObject?.metadata.height as number,
      }
    : originalImage;

  const originalImageUrl = isNewMediaSystem ? originalMediaObject?.originalUrl : originalImage?.uri;

  const applySourceSet = (sourceSetsArg: SourceSetsType, cesdk: CreativeEditorSDK) => {
    const { engine } = cesdk;

    const graphicBlocks = [...engine.block.findByType('graphic'), ...engine.block.findByType('//ly.img.ubq/graphic')];
    const pageBlocks = [...engine.block.findByType('page'), ...engine.block.findByType('//ly.img.ubq/page')];
    let targetBlock = null;

    if (graphicBlocks.length > 0) {
      [targetBlock] = graphicBlocks;
    } else if (pageBlocks.length > 0) {
      [targetBlock] = pageBlocks;
    }

    if (targetBlock && engine.block.supportsFill(targetBlock)) {
      const imageFill = engine.block.getFill(targetBlock);

      if (imageFill) {
        if (isNewMediaSystem) {
          const sourceSet = sourceSetsArg.map((source) => ({
            uri: source.url,
            width: typeof source.width === 'string' ? parseInt(source.width, 10) : source.width,
            height: typeof source.height === 'string' ? parseInt(source.height, 10) : source.height,
          }));

          engine.block.setSourceSet(imageFill, 'fill/image/sourceSet', [originalImageSource as Source, ...sourceSet]);
        }
      } else {
        engine.block.setSourceSet(imageFill, 'fill/image/sourceSet', oldMediaSystemEditingSourceSet as Source[]);
      }
    }
  };

  const saveImage = async (cesdk: CreativeEditorSDK) => {
    const { engine } = cesdk;
    const dialog = cesdk.ui.showDialog({
      type: 'loading',
      clickOutsideToClose: false,
      actions: {
        label: 'Okay',
        onClick: () => {
          cesdk.ui.closeDialog(dialog);
        },
      },
      content: {
        title: 'Saving image...',
        message: 'Do not close this window.',
      },
      progress: 'indeterminate',
    });

    const page = engine.scene.getCurrentPage()!;
    const fill = engine.block.getFill(page);
    const sourceSet = engine.block.getSourceSet(fill, 'fill/image/sourceSet');

    try {
      window.pendo.track('[CE.SDK] Editor Export Called', { fileType: 'image' });

      if (!isNewMediaSystem) {
        engine.block.setSourceSet(fill, 'fill/image/sourceSet', oldMediaSystemExportSourceSet as Source[]);
      }

      // export the canvas:
      const blob = await engine.block.export(page, 'image/jpeg', {
        jpegQuality: 0.9,
      });

      if (blob) {
        window.pendo.track('[CE.SDK] Editor Export', { fileType: 'image' });

        await fetchWithHeaders({
          method: 'POST',
          url: getUrlWithCredentialsFromUser(`https://${WEBPACK_API}/api/v2/error_log.json`, user),
          params: {
            message: `[CE.SDK] Editor Export from domain: ${domainUrl}`,
            stacktrace: 'n/a',
            source: 'web',
            type: 'usage-tracking',
          },
        });

        const file = new File([blob], fileName, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });

        if (isNewMediaSystem) {
          uploadFiles({
            files: [file],
            onUploadStarted: () => Math.random().toString(),
            onMediaCreated: (mediaCreatedObj: OnMediaCreatedFilesArgsType) => {
              const unsubscribe = subscribe(EVENTS.UPLOAD_READY, (mediaObj: MediaType) => {
                if (mediaCreatedObj.mediaId === mediaObj.id) {
                  window.parent.postMessage({ event: EDITOR_EXPORT, originalMediaId: data.originalMediaId, media: mediaObj }, '*');
                  window.parent.postMessage({ event: EDITOR_CLOSE }, '*');
                  cesdk.ui.closeDialog(dialog);
                  resetState();
                  unsubscribe();
                }
              });
            },
          });
        } else {
          window.parent.postMessage({ event: EDITOR_EXPORT, blob }, '*');
          resetState();
          engine.block.setSourceSet(fill, 'fill/image/sourceSet', sourceSet);
        }
      } else {
        resetState();
        cesdk.ui.closeDialog(dialog);
        throw new Error('Blob creation failed');
      }
    } catch (error) {
      reportErrorToFaro(error, 'CreativeEditor config');

      cesdk.ui.closeDialog(dialog);
      showToastNotification({
        notificationText: t('there_was_an_issue_saving') as string,
        type: NotificationTypeEnum.Error,
        showCloseButton: true,
      });
    }
  };

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const presetSubset = customCropPresets.filter((preset) => {
    const { group } = preset.meta;

    if (restrictedGroups.has(group)) return false;

    if (serviceSet != null && serviceSet !== 'message') {
      const allowed = (Array.isArray(serviceSet) ? serviceSet : [serviceSet]) as readonly string[];

      return group === 'custom' || allowed.includes(group);
    }

    // no serviceSet — keep everything not restricted
    return true;
  });

  const init = useCallback(
    async (cesdk: CreativeEditorSDK) => {
      await cesdk.addPlugin(new PhotoEditorConfig());
      await cesdk.addPlugin(new BlurAssetSource());
      await cesdk.addPlugin(new ImageColorsAssetSource());
      await cesdk.addPlugin(new ColorPaletteAssetSource());
      await cesdk.addPlugin(new FiltersAssetSource());
      await cesdk.addPlugin(new PagePresetsAssetSource());
      await cesdk.addPlugin(new StickerAssetSource());
      await cesdk.addPlugin(new TextAssetSource());
      await cesdk.addPlugin(new TextComponentAssetSource());
      await cesdk.addPlugin(new TypefaceAssetSource());
      await cesdk.addPlugin(new VectorShapeAssetSource());
      await cesdk.addPlugin(new UploadAssetSources({ include: ['ly.img.image.upload'] }));

      // Load the image into the editor
      await cesdk.createFromImage(originalImageUrl as string);

      // Capture the original dimensions of the image before any crop presets are applied
      const initialPage = cesdk.engine.scene.getCurrentPage();
      const originalImgWidth = initialPage ? cesdk.engine.block.getWidth(initialPage) : originalImageSource?.width || 0;
      const originalImgHeight = initialPage ? cesdk.engine.block.getHeight(initialPage) : originalImageSource?.height || 0;

      const uploadButton = {
        id: 'ly.img.assetLibrary.dock',
        key: 'upload',
        label: 'Upload',
        icon: '@imgly/Upload',
        entries: ['ly.img.upload'],
      };

      const isLayout = (e: unknown): boolean => {
        const id = typeof e === 'string' ? e : (e as { id?: string })?.id;

        return id === 'ly.img.spacer' || id === 'ly.img.separator';
      };

      const dockOrderOriginal = cesdk.ui
        .getComponentOrder({ in: 'ly.img.dock' })
        .filter((e) => typeof e === 'string' || (e as { key?: string }).key !== 'upload'); // remove any prior upload

      let insertAt = dockOrderOriginal.findIndex((e) => !isLayout(e));

      if (insertAt === -1) insertAt = dockOrderOriginal.length;
      dockOrderOriginal.splice(insertAt, 0, uploadButton);

      cesdk.ui.setComponentOrder({ in: 'ly.img.dock' }, dockOrderOriginal);
      cesdk.ui.setComponentOrder({ in: 'ly.img.navigation.bar' }, [
        'ly.img.undoRedo.navigationBar',
        'ly.img.spacer',
        'ly.img.zoom.navigationBar',
        {
          id: 'ly.img.close.navigationBar',
          label: 'Close',
          icon: '@imgly/Cross',
          variant: 'regular',
          onClick: () => {
            window.pendo.track('[CE.SDK] Editor Close', { fileType: 'image' });
            window.parent.postMessage({ event: EDITOR_CLOSE }, '*');
            resetState();
          },
        },
        {
          id: 'ly.img.action.navigationBar',
          key: 'actions.export.custom_label',
          label: 'Save',
          color: 'accent',
          onClick: async () => {
            saveImage(cesdk);
          },
        },
      ]);

      const dockOrder = cesdk.ui.getComponentOrder({ in: 'ly.img.dock' });

      cesdk.ui.setComponentOrder(
        { in: 'ly.img.dock' },
        dockOrder.filter((entry) => typeof entry === 'string' || entry.key !== 'ly.img.effects')
      );

      cesdk.feature.set('ly.img.inspector.bar', ({ engine }) => {
        return engine.editor.getEditMode() === 'Crop';
      });

      const MY_SOURCE_ID = 'my-social-presets';
      const APPLY_SOURCE_ID = 'my-social-presets-src';

      cesdk.engine.asset.addLocalSource(APPLY_SOURCE_ID);

      presetSubset.forEach((asset) => {
        cesdk.engine.asset.addAssetToSource(APPLY_SOURCE_ID, asset as AssetDefinition);
      });

      cesdk.engine.asset.addLocalSource(MY_SOURCE_ID, undefined, undefined, async (asset, block) => {
        const { engine } = cesdk;
        const page = engine.scene.getCurrentPage();

        await cesdk.ui.applyForceCrop(block, {
          sourceId: APPLY_SOURCE_ID,
          presetId: asset.id,
          mode: 'silent',
        });

        if (page && originalImgWidth > 0 && originalImgHeight > 0) {
          const newWidth = engine.block.getWidth(page);
          const newHeight = engine.block.getHeight(page);

          // Calculate scale to fit the cropped canvas within the absolute original dimensions
          const scale = Math.min(originalImgWidth / newWidth, originalImgHeight / newHeight);

          // If the new crop dimensions exceed the physical original image size, scale down
          if (scale < 1) {
            engine.block.setWidth(page, newWidth * scale);
            engine.block.setHeight(page, newHeight * scale);
          }
        }

        cesdk.engine.block.select(block);
        cesdk.engine.editor.setEditMode('Crop');
      });

      presetSubset.forEach((asset) => {
        cesdk.engine.asset.addAssetToSource(MY_SOURCE_ID, asset as AssetDefinition);
      });

      cesdk.ui.updateAssetLibraryEntry('ly.img.cropPresets', { sourceIds: [MY_SOURCE_ID] });
      cesdk.ui.updateAssetLibraryEntry('ly.img.pagePresets', { sourceIds: [MY_SOURCE_ID] });

      const sourcesetCheck = setInterval(() => {
        if (mediaRef.current.isSuccess && mediaRef.current.media?.length && mediaRef.current.media[0].sourceSets?.length) {
          applySourceSet(mediaRef.current.media[0].sourceSets, cesdk);
          clearInterval(sourcesetCheck);
        }
      }, 1000);
    },
    [originalImageUrl, isNewMediaSystem]
  );

  return (
    <div className="cesdkWrapperStyle">
      {createPortal(
        <CeSDKWrapper>
          <MemoCreativeEditor
            config={{ baseURL: '/cesdk', license: 'VHjEPgPLBuDp-hnJmlQe-UAEu1GdyVUXuNAyIuvheFkzoxpkPdl4A1oKbCGgmjsl', theme: 'dark' }}
            init={init}
          />
        </CeSDKWrapper>,
        document.getElementById('modal-root') as HTMLElement
      )}
    </div>
  );
};

export default Editor;
